import React, { useState, useRef, useEffect } from 'react';
import { usePortfolio } from '../context/PortfolioContext';
import { ChevronDown, Check, Briefcase, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';

const PortfolioSwitcher = () => {
  // FIX: activePortfolioId and switchActivePortfolio are the correct names from your context
  const { portfolios, activePortfolioId, switchActivePortfolio, isLoading, createPortfolio } = usePortfolio();
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [newPortfolioDesc, setNewPortfolioDesc] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCreatePortfolio = async (e) => {
    e.preventDefault();
    
    if (!newPortfolioName.trim()) {
      toast.error('Please enter a portfolio name');
      return;
    }

    setIsCreating(true);
    const loadingToast = toast.loading('Creating portfolio...');

    try {
      await createPortfolio(newPortfolioName.trim(), newPortfolioDesc.trim());
      toast.success('Portfolio created successfully!', { id: loadingToast });
      setShowCreateModal(false);
      setNewPortfolioName('');
      setNewPortfolioDesc('');
      setIsOpen(false);
    } catch (error) {
      toast.error(`Failed to create portfolio: ${error.message}`, { id: loadingToast });
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-12 w-full bg-slate-800/50 animate-pulse rounded-lg border border-slate-700"></div>
    );
  }

  const activePortfolio = portfolios.find(p => p.id === activePortfolioId);

  return (
    <>
      <div className="relative w-full" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full flex items-center justify-between p-2.5 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-700 hover:border-slate-600 transition-all group shadow-sm"
        >
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="w-8 h-8 rounded-md bg-blue-500/10 text-blue-400 flex items-center justify-center shrink-0 border border-blue-500/20 group-hover:border-blue-500/40 transition-colors">
              <Briefcase size={16} />
            </div>
            <div className="flex flex-col items-start overflow-hidden">
              <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Portfolio</span>
              <span className="text-sm font-bold text-slate-200 truncate w-full text-left block">
                {activePortfolio ? activePortfolio.name : 'Select Portfolio'}
              </span>
            </div>
          </div>
          <ChevronDown 
            size={16} 
            className={`text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180 text-blue-400' : ''}`} 
          />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 w-full mt-2 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden ring-1 ring-black/20">
            <div className="p-1.5">
              <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-0.5">
                {portfolios.length > 0 ? (
                  portfolios.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => {
                        switchActivePortfolio(p.id); // FIX: Use the correct context function
                        setIsOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-all ${
                        activePortfolioId === p.id 
                          ? 'bg-blue-600 text-white shadow-sm' 
                          : 'text-slate-300 hover:bg-slate-700/50 hover:text-white'
                      }`}
                    >
                      <span className="truncate font-medium">{p.name}</span>
                      {activePortfolioId === p.id && <Check size={14} className="shrink-0" />}
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-3 text-xs text-slate-500 text-center italic">
                    No portfolios found.
                  </div>
                )}
              </div>
            </div>
            
            <div className="border-t border-slate-700/50 p-1.5 bg-slate-900/30">
              <button 
                onClick={() => {
                  setShowCreateModal(true);
                  setIsOpen(false);
                }}
                className="flex items-center justify-center gap-2 text-xs font-bold text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 px-3 py-2 rounded-md transition-colors w-full"
              >
                <Plus size={14} /> Create New Portfolio
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Portfolio Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
          onClick={() => !isCreating && setShowCreateModal(false)}
        >
          <div 
            className="bg-white w-full max-w-md rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="text-lg font-bold text-slate-900">Create Portfolio</h3>
              <button 
                onClick={() => !isCreating && setShowCreateModal(false)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
                disabled={isCreating}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreatePortfolio} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Portfolio Name *
                </label>
                <input
                  type="text"
                  value={newPortfolioName}
                  onChange={(e) => setNewPortfolioName(e.target.value)}
                  placeholder="e.g., Retirement, Trading, Growth"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  disabled={isCreating}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={newPortfolioDesc}
                  onChange={(e) => setNewPortfolioDesc(e.target.value)}
                  placeholder="Add notes about this portfolio..."
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none transition-colors"
                  disabled={isCreating}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating || !newPortfolioName.trim()}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors shadow-sm"
                >
                  {isCreating ? 'Creating...' : 'Create Portfolio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default PortfolioSwitcher;